import type { FieldErrors } from '@core/types/api.types';

/** Base application error — everything thrown by the network layer extends this. */
export class AppError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AppError';
  }
}

/** An error response returned by the server with an HTTP status code. */
export class ApiError extends AppError {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors?: FieldErrors;

  constructor(status: number, code: string, message: string, fieldErrors?: FieldErrors) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

/** Validation failure (HTTP 422) carrying per-field messages for forms. */
export class ValidationError extends ApiError {
  constructor(message: string, fieldErrors: FieldErrors) {
    super(422, 'validation_error', message, fieldErrors);
    this.name = 'ValidationError';
  }
}

/** No response reached (offline, DNS, timeout, CORS). */
export class NetworkError extends AppError {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}
